import moment from "moment";
import React, { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";

// react-datepicker clones this element and injects `value` and `onClick`, so it
// has to be a stable component type. Declaring it inside PickDate made it a new
// type on every render, which remounted the button underneath the user — and
// dropped the click that was meant to open the calendar.
const PickDateInput = forwardRef(
  (
    {
      value,
      onClick,
      date,
      required,
      editable,
      isReview,
      onLeaveReview,
      iconSize,
    },
    ref
  ) => {
    // One click does both jobs: it takes a review-mode field out of review and
    // opens the calendar. The handler is always a function — passing `false`
    // here (which is what `setDate && !isReview && onClick` evaluated to while
    // in review mode) makes React throw "func.apply is not a function" the
    // moment the button is clicked.
    const handleClick = (event) => {
      if (!editable) return;
      if (isReview) onLeaveReview();
      if (onClick) onClick(event);
    };

    return (
      <button
        type="button"
        className={
          `d-flex align-items-center justify-content-between w-100 px-2 py-1 rounded ` +
          (isReview ? `bg-gray-disabled ` : `bg-white `) +
          (isReview ? "border-0" : value ? `datePicker-active` : `datePicker`)
        }
        onClick={handleClick}
        ref={ref}
      >
        <small>
          {value ? (
            moment(date).format("D MMM YYYY")
          ) : (
            <b>
              <sup className="text_danger">{!required ? "" : "*"}</sup>
            </b>
          )}
        </small>
        <div className="d-flex align-items-center">
          <small className="d-flex align-items-center">
            <i
              className={`${
                isReview ? "fa fa-edit fs-18" : "fa fa-calendar"
              } text_primary`}
              style={{ fontSize: iconSize }}
            />
          </small>
        </div>
      </button>
    );
  }
);

const PickDate = ({
  date,
  setDate,
  required = true,
  review = false,
  maxDate,
  iconSize = 18,
}) => {
  const [isReview, setIsReview] = useState(review);
  useEffect(() => {
    moment.updateLocale("en", {
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
    });
  }, []);

  // Without a setter there is nothing to pick, so the field stays read-only.
  const editable = typeof setDate === "function";

  return (
    <DatePicker
      selected={date}
      onChange={(nextDate) => editable && setDate(nextDate)}
      customInput={
        <PickDateInput
          date={date}
          required={required}
          editable={editable}
          isReview={isReview}
          onLeaveReview={() => setIsReview(false)}
          iconSize={iconSize}
        />
      }
      dateFormat="dd MMM yyyy"
      maxDate={maxDate}
    />
  );
};

export default PickDate;
